import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { 
  Building2, 
  MapPin, 
  DollarSign, 
  Scale, 
  FileText,
  Users,
  Save,
  Check,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Search,
  Plus,
  Trash2,
  Settings,
  Loader2,
  Globe,
  Briefcase,
  Info
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { PageLoader } from '../components/common/LoadingSpinner';
import { JurisdictionSelector } from '../components/common/JurisdictionSelector';
import { toast } from 'sonner';

export const FirmSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [firmSettings, setFirmSettings] = useState(null);
  const [practiceAreas, setPracticeAreas] = useState([]);
  const [chargeCodes, setChargeCodes] = useState([]);
  const [teamRates, setTeamRates] = useState([]);
  const [jurisdictions, setJurisdictions] = useState([]);
  const [selectedPracticeArea, setSelectedPracticeArea] = useState(null);
  const [codeSearchTerm, setCodeSearchTerm] = useState('');
  const [showCustomCodeDialog, setShowCustomCodeDialog] = useState(false);
  const [newCustomCode, setNewCustomCode] = useState({
    code: '',
    name: '',
    description: '',
    practice_areas: [],
    subcategories: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, areasRes, jurisdictionsRes, ratesRes] = await Promise.all([
        api.get('/firm/settings/full'),
        api.get('/practice-areas'),
        api.get('/jurisdictions'),
        api.get('/team/rates')
      ]);
      
      setFirmSettings(settingsRes.data);
      setPracticeAreas(areasRes.data);
      // Handle the new response format - jurisdictions is now an object with jurisdictions array
      const jurisdictionsData = jurisdictionsRes.data.jurisdictions || jurisdictionsRes.data;
      setJurisdictions(Array.isArray(jurisdictionsData) ? jurisdictionsData : []);
      setTeamRates(ratesRes.data);
      
      // Fetch charge codes if practice areas are enabled
      if (settingsRes.data.enabled_practice_areas?.length > 0) {
        const codesRes = await api.get('/charge-codes');
        setChargeCodes(codesRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch firm settings:', error);
      toast.error('Failed to load firm settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      // Only send the fields that the backend expects
      const updateData = {
        name: firmSettings.name,
        address: firmSettings.address,
        phone: firmSettings.phone,
        email: firmSettings.email,
        jurisdiction: firmSettings.jurisdiction,
        enabled_practice_areas: firmSettings.enabled_practice_areas,
        hourly_rates: firmSettings.hourly_rates,
        default_retainer_amount: firmSettings.default_retainer_amount,
        auto_send_retainer: firmSettings.auto_send_retainer,
        billing_in_reports: firmSettings.billing_in_reports,
        default_court: firmSettings.default_court,
      };
      
      await api.put('/firm/settings/full', updateData);
      toast.success('Firm settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const togglePracticeArea = (areaId) => {
    const currentEnabled = firmSettings.enabled_practice_areas || [];
    const newEnabled = currentEnabled.includes(areaId)
      ? currentEnabled.filter(id => id !== areaId)
      : [...currentEnabled, areaId];
    
    setFirmSettings(prev => ({
      ...prev,
      enabled_practice_areas: newEnabled
    }));
  };

  const toggleChargeCode = async (code, enabled) => {
    try {
      await api.put(`/charge-codes/${code}/toggle`, { enabled });
      setChargeCodes(prev => prev.map(c => 
        c.code === code ? { ...c, enabled } : c
      ));
      toast.success(`Charge code ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update charge code');
    }
  };

  const handleRateChange = (role, value) => {
    setFirmSettings(prev => ({
      ...prev,
      hourly_rates: {
        ...prev.hourly_rates,
        [role]: parseFloat(value) || 0
      }
    }));
  };

  const updateTeamMemberRate = async (userId, rate) => {
    try {
      await api.put(`/team/${userId}/hourly-rate`, { hourly_rate: parseFloat(rate) });
      setTeamRates(prev => prev.map(m => 
        m.user_id === userId ? { ...m, hourly_rate: parseFloat(rate) } : m
      ));
      toast.success('Team member rate updated');
    } catch (error) {
      toast.error('Failed to update rate');
    }
  };

  const handleCreateCustomCode = async () => {
    try {
      await api.post('/charge-codes/custom', newCustomCode);
      toast.success('Custom charge code created');
      setShowCustomCodeDialog(false);
      setNewCustomCode({ code: '', name: '', description: '', practice_areas: [], subcategories: [] });
      // Refresh charge codes
      const codesRes = await api.get('/charge-codes');
      setChargeCodes(codesRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create charge code');
    }
  };

  const filteredChargeCodes = chargeCodes.filter(code => {
    if (codeSearchTerm) {
      const term = codeSearchTerm.toLowerCase();
      return code.code.toLowerCase().includes(term) || 
             code.name.toLowerCase().includes(term) ||
             code.description?.toLowerCase().includes(term);
    }
    if (selectedPracticeArea) {
      return code.practice_areas?.includes(selectedPracticeArea);
    }
    return true;
  });

  if (loading) {
    return <PageLoader text="Loading firm settings..." />;
  }

  const enabledAreas = firmSettings?.enabled_practice_areas || [];
  const setupComplete = enabledAreas.length > 0;

  return (
    <div className="space-y-6" data-testid="firm-settings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Firm Settings</h1>
          <p className="text-slate-500 mt-1">
            Configure your firm&apos;s jurisdiction, practice areas, and billing rates
          </p>
        </div>
        <Button onClick={handleSaveSettings} disabled={saving} data-testid="save-settings-btn">
          {saving ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
          ) : (
            <><Save className="h-4 w-4 mr-2" /> Save Settings</>
          )}
        </Button>
      </div>

      {/* Setup Warning */}
      {!setupComplete && (
        <Alert className="bg-amber-50 border-amber-200">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">Setup Required</AlertTitle>
          <AlertDescription className="text-amber-700">
            Please select your jurisdiction and enable at least one practice area to use case creation.
          </AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="practice-areas">Practice Areas</TabsTrigger>
          <TabsTrigger value="charge-codes">Charges & Codes</TabsTrigger>
          <TabsTrigger value="billing">Billing Rates</TabsTrigger>
          <TabsTrigger value="team-rates">Team Rates</TabsTrigger>
          <TabsTrigger value="payments">Payments</TabsTrigger>
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Firm Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Firm Name</Label>
                  <Input
                    value={firmSettings?.name || ''}
                    onChange={(e) => setFirmSettings(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter firm name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input
                    value={firmSettings?.address || ''}
                    onChange={(e) => setFirmSettings(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="Enter address"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={firmSettings?.phone || ''}
                      onChange={(e) => setFirmSettings(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="Phone number"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={firmSettings?.email || ''}
                      onChange={(e) => setFirmSettings(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="Email address"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Jurisdiction
                </CardTitle>
                <CardDescription>
                  Your jurisdiction determines court rules, deadlines, and available charge codes
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Region / Jurisdiction *</Label>
                  <p className="text-xs text-slate-500 mb-2">
                    Search or select your firm's primary operating jurisdiction
                  </p>
                  <JurisdictionSelector
                    value={firmSettings?.jurisdiction || 'ontario'}
                    onChange={(value) => setFirmSettings(prev => ({ ...prev, jurisdiction: value }))}
                    placeholder="Search and select jurisdiction..."
                    apiEndpoint="firm"
                  />
                  {firmSettings?.jurisdiction && (
                    <p className="text-xs text-green-600 flex items-center gap-1 mt-2">
                      <Check className="h-3 w-3" />
                      Jurisdiction set - court rules and deadlines will use this region
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Default Court</Label>
                  <Select 
                    value={firmSettings?.default_court || ''}
                    onValueChange={(value) => setFirmSettings(prev => ({ ...prev, default_court: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select default court" />
                    </SelectTrigger>
                    <SelectContent>
                      {jurisdictions.find(j => j.id === firmSettings?.jurisdiction)?.courts?.map((court) => (
                        <SelectItem key={court} value={court}>{court}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="pt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Auto-Send Retainer</Label>
                      <p className="text-xs text-slate-500">Automatically send retainer when client is created</p>
                    </div>
                    <Switch 
                      checked={firmSettings?.auto_send_retainer ?? true}
                      onCheckedChange={(checked) => setFirmSettings(prev => ({ ...prev, auto_send_retainer: checked }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Default Retainer Amount ($)</Label>
                    <Input
                      type="number"
                      value={firmSettings?.default_retainer_amount || 5000}
                      onChange={(e) => setFirmSettings(prev => ({ ...prev, default_retainer_amount: parseFloat(e.target.value) }))}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Practice Areas */}
        <TabsContent value="practice-areas">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scale className="h-5 w-5" />
                Practice Areas
              </CardTitle>
              <CardDescription>
                Enable the practice areas your firm handles. This controls what options are available when creating cases.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-slate-500">
                    {enabledAreas.length} of {practiceAreas.length} practice areas enabled
                  </p>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setFirmSettings(prev => ({ ...prev, enabled_practice_areas: [] }))}
                    >
                      Disable All
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setFirmSettings(prev => ({ 
                        ...prev, 
                        enabled_practice_areas: practiceAreas.map(a => a.id) 
                      }))}
                    >
                      Enable All
                    </Button>
                  </div>
                </div>

                <Accordion type="multiple" className="w-full">
                  {practiceAreas.map((area) => {
                    const isEnabled = enabledAreas.includes(area.id);
                    return (
                      <AccordionItem key={area.id} value={area.id}>
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center gap-3 flex-1">
                            <Switch 
                              checked={isEnabled}
                              onCheckedChange={() => togglePracticeArea(area.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <Briefcase className={`h-4 w-4 ${isEnabled ? 'text-blue-600' : 'text-slate-400'}`} />
                            <span className={isEnabled ? 'font-medium' : 'text-slate-500'}>{area.name}</span>
                            <Badge variant={isEnabled ? "default" : "secondary"} className="ml-2">
                              {area.subcategories?.length || 0} subcategories
                            </Badge>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="pl-12 pt-2 space-y-2">
                            <p className="text-sm text-slate-500 mb-3">{area.description}</p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                              {area.subcategories?.map((sub) => (
                                <div 
                                  key={sub.id} 
                                  className={`p-2 rounded border text-sm ${
                                    isEnabled ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'
                                  }`}
                                >
                                  {sub.name}
                                </div>
                              ))}
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Charge Codes */}
        <TabsContent value="charge-codes">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Charges & Codes
                  </CardTitle>
                  <CardDescription>
                    Auto-sourced from Ontario legal authorities. Enable/disable codes for your firm.
                  </CardDescription>
                </div>
                <Dialog open={showCustomCodeDialog} onOpenChange={setShowCustomCodeDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Custom Code
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create Custom Charge Code</DialogTitle>
                      <DialogDescription>
                        Add a firm-specific charge code
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Code *</Label>
                          <Input
                            placeholder="e.g., FIRM-001"
                            value={newCustomCode.code}
                            onChange={(e) => setNewCustomCode(prev => ({ ...prev, code: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Name *</Label>
                          <Input
                            placeholder="Code name"
                            value={newCustomCode.name}
                            onChange={(e) => setNewCustomCode(prev => ({ ...prev, name: e.target.value }))}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Input
                          placeholder="Description of the charge"
                          value={newCustomCode.description}
                          onChange={(e) => setNewCustomCode(prev => ({ ...prev, description: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Practice Areas</Label>
                        <Select
                          value={newCustomCode.practice_areas[0] || ''}
                          onValueChange={(value) => setNewCustomCode(prev => ({ 
                            ...prev, 
                            practice_areas: [value] 
                          }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select practice area" />
                          </SelectTrigger>
                          <SelectContent>
                            {practiceAreas.filter(a => enabledAreas.includes(a.id)).map((area) => (
                              <SelectItem key={area.id} value={area.id}>{area.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowCustomCodeDialog(false)}>Cancel</Button>
                      <Button onClick={handleCreateCustomCode} disabled={!newCustomCode.code || !newCustomCode.name}>
                        Create Code
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="flex gap-4 mb-6">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search codes..."
                    value={codeSearchTerm}
                    onChange={(e) => setCodeSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select 
                  value={selectedPracticeArea || 'all'}
                  onValueChange={(value) => setSelectedPracticeArea(value === 'all' ? null : value)}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Filter by area" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Practice Areas</SelectItem>
                    {practiceAreas.filter(a => enabledAreas.includes(a.id) || enabledAreas.length === 0).map((area) => (
                      <SelectItem key={area.id} value={area.id}>{area.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Codes List */}
              <div className="border rounded-lg max-h-[500px] overflow-y-auto">
                {filteredChargeCodes.length > 0 ? (
                  <table className="w-full">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium">Code</th>
                        <th className="text-left p-3 text-sm font-medium">Name</th>
                        <th className="text-left p-3 text-sm font-medium hidden md:table-cell">Source</th>
                        <th className="text-left p-3 text-sm font-medium hidden lg:table-cell">Practice Area</th>
                        <th className="text-center p-3 text-sm font-medium">Enabled</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredChargeCodes.map((code) => (
                        <tr key={code.code} className="border-t hover:bg-slate-50">
                          <td className="p-3">
                            <span className="font-mono text-sm">{code.code}</span>
                            {code.is_custom && (
                              <Badge variant="outline" className="ml-2 text-xs">Custom</Badge>
                            )}
                          </td>
                          <td className="p-3">
                            <div>
                              <p className="font-medium text-sm">{code.name}</p>
                              <p className="text-xs text-slate-500 truncate max-w-[200px]">{code.description}</p>
                            </div>
                          </td>
                          <td className="p-3 hidden md:table-cell">
                            <span className="text-xs text-slate-500">{code.source}</span>
                          </td>
                          <td className="p-3 hidden lg:table-cell">
                            <div className="flex flex-wrap gap-1">
                              {code.practice_areas?.slice(0, 2).map((area) => (
                                <Badge key={area} variant="secondary" className="text-xs">
                                  {practiceAreas.find(a => a.id === area)?.name || area}
                                </Badge>
                              ))}
                            </div>
                          </td>
                          <td className="p-3 text-center">
                            <Switch 
                              checked={code.enabled}
                              onCheckedChange={(enabled) => toggleChargeCode(code.code, enabled)}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No charge codes found</p>
                    <p className="text-sm">Try adjusting your filters or enable practice areas first</p>
                  </div>
                )}
              </div>
              
              <div className="mt-4 flex items-center gap-2 text-sm text-slate-500">
                <Info className="h-4 w-4" />
                <span>Showing {filteredChargeCodes.length} codes. Official codes are auto-sourced from government authorities.</span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Billing Rates */}
        <TabsContent value="billing">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Default Billing Rates
              </CardTitle>
              <CardDescription>
                Set default hourly rates by role. These are automatically assigned to new team members.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(firmSettings?.hourly_rates || {}).map(([role, rate]) => (
                  <div key={role} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="capitalize">{role.replace('_', ' ')}</Label>
                      <Badge variant="outline">/hour</Badge>
                    </div>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500">$</span>
                      <Input
                        type="number"
                        value={rate}
                        onChange={(e) => handleRateChange(role, e.target.value)}
                        className="pl-8"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Team Rates */}
        <TabsContent value="team-rates">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Team Member Rates
              </CardTitle>
              <CardDescription>
                View and update hourly rates for individual team members
              </CardDescription>
            </CardHeader>
            <CardContent>
              {teamRates.length > 0 ? (
                <div className="space-y-3">
                  {teamRates.map((member) => (
                    <div key={member.user_id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-blue-600 font-medium">
                            {member.name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{member.name}</p>
                          <p className="text-sm text-slate-500">{member.email}</p>
                        </div>
                        <Badge variant="outline" className="capitalize ml-2">{member.role}</Badge>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="relative w-32">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500">$</span>
                          <Input
                            type="number"
                            value={member.hourly_rate || 0}
                            onChange={(e) => updateTeamMemberRate(member.user_id, e.target.value)}
                            className="pl-8"
                          />
                        </div>
                        <span className="text-sm text-slate-500">/hr</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <Users className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No team members found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Tab - Stripe Connect */}
        <TabsContent value="payments">
          <StripeConnectSection />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Stripe Connect Section Component
const StripeConnectSection = () => {
  const [stripeStatus, setStripeStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    fetchStripeStatus();
    
    // Check URL params for callback
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe') === 'success') {
      refreshStatus();
      toast.success('Stripe account connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('stripe') === 'refresh') {
      refreshStatus();
      toast.info('Please complete Stripe onboarding');
    }
  }, []);

  const fetchStripeStatus = async () => {
    try {
      const res = await api.get('/stripe/connect/status');
      setStripeStatus(res.data);
    } catch (error) {
      console.error('Failed to fetch Stripe status:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshStatus = async () => {
    try {
      const res = await api.post('/stripe/connect/refresh');
      setStripeStatus(res.data);
    } catch (error) {
      console.error('Failed to refresh status:', error);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await api.post('/stripe/connect/onboard', {
        origin_url: window.location.origin
      });
      window.location.href = res.data.url;
    } catch (error) {
      console.error('Failed to start Stripe onboarding:', error);
      toast.error(error.response?.data?.detail || 'Failed to connect Stripe');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect your Stripe account? You will not be able to accept payments until you reconnect.')) {
      return;
    }
    try {
      await api.post('/stripe/connect/disconnect');
      setStripeStatus({ connected: false });
      toast.success('Stripe account disconnected');
    } catch (error) {
      toast.error('Failed to disconnect Stripe');
    }
  };

  const handleOpenDashboard = async () => {
    try {
      const res = await api.get('/stripe/connect/dashboard');
      window.open(res.data.url, '_blank');
    } catch (error) {
      toast.error('Failed to open Stripe dashboard');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stripe Connect Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
              <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z" fill="#6772E5"/>
            </svg>
            Accept Payments with Stripe
          </CardTitle>
          <CardDescription>
            Connect your Stripe account to accept credit card payments from your clients
          </CardDescription>
        </CardHeader>
        <CardContent>
          {stripeStatus?.connected ? (
            <div className="space-y-4">
              {/* Status */}
              <div className="flex items-center gap-3 p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Check className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium text-emerald-900">Stripe Connected</p>
                  <p className="text-sm text-emerald-600">Account ID: {stripeStatus.account_id}</p>
                </div>
              </div>

              {/* Capabilities */}
              <div className="grid grid-cols-2 gap-4">
                <div className={`p-4 rounded-lg border ${stripeStatus.charges_enabled ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                  <div className="flex items-center gap-2">
                    {stripeStatus.charges_enabled ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                    )}
                    <span className={`font-medium ${stripeStatus.charges_enabled ? 'text-green-900' : 'text-amber-900'}`}>
                      {stripeStatus.charges_enabled ? 'Charges Enabled' : 'Charges Pending'}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {stripeStatus.charges_enabled ? 'You can accept payments' : 'Complete onboarding to accept payments'}
                  </p>
                </div>
                <div className={`p-4 rounded-lg border ${stripeStatus.payouts_enabled ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                  <div className="flex items-center gap-2">
                    {stripeStatus.payouts_enabled ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                    )}
                    <span className={`font-medium ${stripeStatus.payouts_enabled ? 'text-green-900' : 'text-amber-900'}`}>
                      {stripeStatus.payouts_enabled ? 'Payouts Enabled' : 'Payouts Pending'}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {stripeStatus.payouts_enabled ? 'Funds will be deposited to your bank' : 'Add bank account in Stripe'}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button onClick={handleOpenDashboard} variant="outline" className="flex-1">
                  Open Stripe Dashboard
                </Button>
                <Button onClick={handleConnect} variant="outline">
                  Update Settings
                </Button>
                <Button onClick={handleDisconnect} variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                  Disconnect
                </Button>
              </div>

              {!stripeStatus.charges_enabled && (
                <Alert className="bg-amber-50 border-amber-200">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertTitle className="text-amber-900">Complete Your Setup</AlertTitle>
                  <AlertDescription className="text-amber-700">
                    Your Stripe account needs additional information before you can accept payments.
                    <Button onClick={handleConnect} variant="link" className="text-amber-700 underline p-0 h-auto ml-1">
                      Complete onboarding →
                    </Button>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 rounded-lg bg-slate-50 border">
                <div className="h-12 w-12 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  <DollarSign className="h-6 w-6 text-indigo-600" />
                </div>
                <div>
                  <h4 className="font-medium">Get Paid Faster</h4>
                  <p className="text-sm text-slate-500 mt-1">
                    Connect your Stripe account to accept credit card payments directly from your invoices. 
                    Funds are deposited directly to your bank account.
                  </p>
                  <ul className="mt-3 space-y-1 text-sm text-slate-600">
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500" />
                      Accept Visa, Mastercard, Amex
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500" />
                      Automatic payment tracking
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500" />
                      Secure, PCI compliant
                    </li>
                  </ul>
                </div>
              </div>
              
              <Button 
                onClick={handleConnect} 
                disabled={connecting}
                className="w-full bg-indigo-600 hover:bg-indigo-700"
              >
                {connecting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
                    </svg>
                    Connect with Stripe
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Payment Processing Fees</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-slate-600 space-y-2">
            <p>Stripe charges standard payment processing fees:</p>
            <ul className="list-disc list-inside space-y-1 text-slate-500">
              <li>2.9% + $0.30 per successful card charge</li>
              <li>No monthly fees or setup costs</li>
              <li>Funds typically arrive in 2 business days</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FirmSettings;
